/**
 * Taking down the per-state animation bindings a state delete leaves behind.
 *
 * `setClipForState` binds an AnimationSequence slot's `src` to a
 * `kind: 'animation-clip'` declaration (`$animMain`) so a state can swap the
 * clip. Once an object has no state left the binding swaps nothing: it saves
 * with the scene, and a state added later would resolve the slot to the
 * declaration's default and bring a stale clip back. The delete's undo entry
 * carries this teardown so one undo restores the state and its bindings
 * together.
 *
 * Kept beside `animation-bindings.ts`, which owns the binding, and not in it:
 * that file imports `states/statesActions`, which calls this.
 */

import type { SceneObject } from '../core/SceneObject';
import type { AnimationSequenceStates, AnimationStateConfig } from '../scenedata/behaviors/animationsequencedata';
import type { PropertyDeclaration } from '../scenedata/behaviors/propertiesdata';
import { boundPropertyName, isPropertyBinding } from '../behaviors/tracebehavior';
import { AnimationSequence } from '../behaviors/animationsequence';
import { Properties } from '../behaviors/properties';
import { Logger } from '../utils/Logger';
import { BehaviorCommitCommand } from './undo/BehaviorCommitCommand';
import type { UndoCommand } from './undo/UndoCommand';
import { dispatchBehaviorChanged } from './undo/editor-events';

/** The slots an AnimationSequence plays, in the order they are collapsed. */
const CLIP_SLOTS = ['enter', 'main', 'exit'] as const;

export interface OrphanedBindingTeardown {
    states: AnimationSequenceStates;
    declarations: PropertyDeclaration[];
}

/**
 * Collapse every `$`-bound slot back to its declaration's literal default and
 * drop the declarations that only backed those bindings. A slot whose default
 * is empty (no base clip) is removed; one with a base clip keeps it as a plain
 * `src`. Null when no slot is bound.
 */
export function planOrphanedBindingTeardown(
    states: AnimationSequenceStates,
    declarations: readonly PropertyDeclaration[],
): OrphanedBindingTeardown | null {
    const boundNames = new Set<string>();
    const next: AnimationSequenceStates = { ...states };
    let changed = false;

    // The literal behind a binding, noting the property so its declaration can go.
    const literalFor = (binding: string): string => {
        const name = boundPropertyName(binding);
        if (name) boundNames.add(name);
        const decl = name ? declarations.find((d) => d.name === name) : undefined;
        return typeof decl?.value === 'string' ? decl.value : '';
    };

    for (const slot of CLIP_SLOTS) {
        const entry = states[slot];
        if (!entry) continue;
        const srcBound = typeof entry.src === 'string' && isPropertyBinding(entry.src);
        const clipBound = typeof entry.clip === 'string' && isPropertyBinding(entry.clip);
        if (!srcBound && !clipBound) continue;

        const collapsed: AnimationStateConfig = { ...entry };
        if (srcBound) {
            const literal = literalFor(entry.src!);
            if (literal) collapsed.src = literal;
            else delete collapsed.src;
        }
        if (clipBound) {
            const literal = literalFor(entry.clip!);
            if (literal) collapsed.clip = literal;
            else delete collapsed.clip;
        }
        // No base `src` plays nothing: the slot goes with it.
        if (collapsed.src) next[slot] = collapsed;
        else delete next[slot];
        changed = true;
    }

    if (!changed) return null;
    return { states: next, declarations: declarations.filter((d) => !boundNames.has(d.name)) };
}

/**
 * Commit the teardown on `obj` and return its undo commands, without pushing
 * them, for the caller to bundle. Empty when `obj` has nothing bound. The
 * caller has checked that `obj` has no state left.
 */
export async function buildOrphanedBindingTeardownCommands(obj: SceneObject): Promise<UndoCommand[]> {
    const sequence = obj.getBehavior(AnimationSequence);
    const properties = obj.getBehavior(Properties);
    if (!sequence || !properties) return [];
    const plan = planOrphanedBindingTeardown(sequence.getData().states, properties.getData().properties ?? []);
    if (!plan) return [];

    const commands: UndoCommand[] = [];
    const sequenceBefore = sequence.getData();
    const sequenceErrors = await sequence.commit({ states: plan.states });
    if (sequenceErrors.length > 0) {
        Logger.warn(`[animation-binding-teardown] AnimationSequence commit on ${obj.name} rejected`, sequenceErrors);
        return commands;
    }
    commands.push(new BehaviorCommitCommand(obj, 'AnimationSequence', sequenceBefore, sequence.getData(), null));
    dispatchBehaviorChanged({ objectPath: obj.getPath(), behaviorType: 'AnimationSequence', action: 'updated' });

    const propertiesBefore = properties.getData();
    const propertyErrors = await properties.commit({ properties: plan.declarations });
    if (propertyErrors.length > 0) {
        Logger.warn(`[animation-binding-teardown] Properties commit on ${obj.name} rejected`, propertyErrors);
        return commands;
    }
    commands.push(new BehaviorCommitCommand(obj, 'Properties', propertiesBefore, properties.getData(), null));
    dispatchBehaviorChanged({ objectPath: obj.getPath(), behaviorType: 'Properties', action: 'updated' });
    return commands;
}
